import { Col, Form, Input, InputNumber, Modal, Row, TreeSelect } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import connectionMonitorApi from '../../../../../../api/ConnectionMonitorApi';
import folderApi from '../../../../../../api/FolderApi';
import SelectProjectComponent from '../../../../../../components/SelectProjectComponent/SelectProjectComponent';
import useProjects from '../../../../../../hooks/ProjectsHook';
import { FolderDataNode } from '../../../../../../model/elements';
import { Connection, ConnectionMonitor } from '../../../../../../model/entities';
import notificationService from '../../../../../../services/NotificationService';
import treeFolderService from '../../../../../../services/TreeFolderService';
import styles from './ConnectionMonitoringModal.module.scss';

/**
 * Returns the connection monitoring modal.
 * @props the props
 * @returns the connection monitoring modal
 */
const ConnectionMonitoringModal = (props: Props): React.ReactElement => {
    const { visible, connection, onCancel, onSave, connectionMonitorId } = props;
    /*** HOOKS ***/

    const [form] = Form.useForm();
    const intl = useIntl();
    const [listProjects, getProject] = useProjects();
    const [connectionMonitor, setConnectionMonitor] = useState<ConnectionMonitor>();
    const [loading, setLoading] = useState<'loading' | 'saving' | 'loadingFolders'>();
    const [sourceFolderNodes, setSourceFolderNodes] = useState<FolderDataNode[]>([]);
    const [sourceFolderNode, setSourceFolderNode] = useState<FolderDataNode>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');

                if (connectionMonitorId) {
                    // get connection monitor
                    const connectionMonitor = await connectionMonitorApi.get(connectionMonitorId);
                    setConnectionMonitor(connectionMonitor);

                    // get source folder node (currently selected)
                    let sourceFolderNode = sourceFolderNodes.find((f) => f.folder.id === connectionMonitor.folder?.id);
                    if (!sourceFolderNode) {
                        try {
                            const sourceFolder = await folderApi.get(
                                connectionMonitor.folder!.id!,
                                connection.source!.provider,
                                connection.source!.id!,
                                connectionMonitor.project!.id!,
                            );
                            sourceFolderNode = treeFolderService.createFolderNode(sourceFolder);
                        } catch (error) {
                            // folder has been deleted
                        }
                    }

                    setSourceFolderNode(sourceFolderNode);

                    // load form values
                    form.setFieldsValue(connectionMonitor);
                    form.setFieldValue(['folder'], sourceFolderNode?.folder.name);
                } else {
                    const connectionMonitor: ConnectionMonitor = { connectionId: connection.id };
                    setConnectionMonitor(connectionMonitor);

                    // load form values
                    form.setFieldsValue(connectionMonitor);
                }
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [connection.id, connection.source, connectionMonitorId, form, intl, sourceFolderNodes]);

    /*** METHODS ***/

    const cancel = () => {
        form.resetFields();
        onCancel();
    };

    const submit = async () => {
        try {
            const values = await form.validateFields();
            await save(values);
        } catch (error) {}
    };

    const save = async (values: any) => {
        try {
            setLoading('saving');

            const project = await getProject(values.project.id);
            const selectedFolderNode =
                (treeFolderService.find(values.folder, sourceFolderNodes) as FolderDataNode) || sourceFolderNode;

            const connectionMonitorUpdated: ConnectionMonitor = Object.assign({}, connectionMonitor, {
                name: values.name,
                delay: values.delay,
                coolingOffPeriod: values.coolingOffPeriod,
                hubId: !project || !selectedFolderNode ? undefined : connection.source?.id,
                provider: !project || !selectedFolderNode ? undefined : connection.source?.provider,
                project: !project || !selectedFolderNode ? undefined : project,
                folder:
                    !project || !selectedFolderNode
                        ? undefined
                        : {
                              id: selectedFolderNode?.folder.id,
                              name:
                                  connectionMonitor?.folder?.id === selectedFolderNode?.folder.id
                                      ? connectionMonitor?.folder?.name
                                      : selectedFolderNode?.path,
                          },
            });
            connectionMonitorUpdated.id
                ? await connectionMonitorApi.update(connectionMonitorUpdated, connection)
                : await connectionMonitorApi.create(connectionMonitorUpdated, connection);
            form.resetFields();
            onSave();
        } catch (error: any) {
            if (error.response.status === 409 && error.response.data) {
                notificationService.displayError(error, intl, [
                    { status: 409, message: 'connectionMonitor.status.duplicate' },
                ]);
            } else {
                notificationService.displayError(error, intl);
            }
        } finally {
            setLoading(undefined);
        }
    };

    const listFolders = async (projectId?: string) => {
        try {
            setLoading('loadingFolders');

            // reset previous selected folder
            form.resetFields(['folder']);

            // load source folders
            const sourceFolders = projectId
                ? await folderApi.list(connection.source!.provider, connection.source!.id!, projectId)
                : [];
            const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));
            setSourceFolderNodes(sourceFolderNodes);
        } catch (error) {
            notificationService.displayError(error, intl);
            setSourceFolderNodes([]);
        } finally {
            setLoading(undefined);
        }
    };

    const loadSubfolderNodes = async (folderId: string) => {
        const sourceFolderNodesAux = [...sourceFolderNodes];
        await treeFolderService.loadSubnodes(folderId, sourceFolderNodesAux);
        setSourceFolderNodes(sourceFolderNodesAux);
    };

    const handleGetProject = useCallback(
        (id: string) => getProject(id, connection.source!.provider, connection.source!.id!),
        [getProject, connection.source],
    );

    const handleListProjects = useCallback(
        (searchText: string) =>
            listProjects(0, 5, 'name', true, connection.source!.id!, connection.source!.provider, searchText),
        [listProjects, connection.source],
    );

    /*** COMPONENTS ***/

    return (
        <Modal
            title={
                connectionMonitorId ? (
                    <FormattedMessage id="connectionMonitor.edit" />
                ) : (
                    <FormattedMessage id="connectionMonitor.create" />
                )
            }
            open={visible}
            onCancel={cancel}
            onOk={submit}
            okButtonProps={{ disabled: loading === 'loading', loading: loading === 'saving' }}
            cancelButtonProps={{ disabled: loading === 'saving' }}
            width={1000}
        >
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={200} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.delay" />}
                            name="delay"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber min={0} size="large" className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.coolingOffPeriod" />}
                            name="coolingOffPeriod"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber min={0} size="large" className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item label={<FormattedMessage id="connectionMonitor.project" />} name={['project', 'id']}>
                            <SelectProjectComponent
                                size="large"
                                showSearch
                                onChange={(projectId) => listFolders(projectId as string)}
                                loading={loading === 'loading'}
                                disabled={!!connectionMonitorId}
                                onList={handleListProjects}
                                onGet={handleGetProject}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<FormattedMessage id="connectionMonitor.folder" />} name={['folder']}>
                            <TreeSelect
                                style={{ width: '100%' }}
                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                treeData={sourceFolderNodes}
                                loadData={(folderNode: any) => loadSubfolderNodes(folderNode.folder.id)}
                                allowClear
                                size="large"
                                loading={loading === 'loading' || loading === 'loadingFolders'}
                                disabled={!!connectionMonitorId}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ConnectionMonitoringModal;

interface Props {
    visible?: boolean;
    connection: Connection;
    connectionMonitorId?: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
